export const FOLDER_SIDEBAR_STORAGE_KEY = 'workflowFoldersSidebarCollapsed';

export const ALL_WORKFLOWS_FOLDER_ID = 'all';
export const ARCHIVED_FOLDER_ID = 'archived';
export const UNFILED_FOLDER_ID = 'unfiled';

export function getInitialFolderSidebarCollapsed() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(FOLDER_SIDEBAR_STORAGE_KEY) === '1';
}

export function persistFolderSidebarCollapsed(collapsed) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(FOLDER_SIDEBAR_STORAGE_KEY, collapsed ? '1' : '0');
}

export function renderText(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object') {
    return (
      value.name
      || value.label
      || value.title
      || value.display_name
      || value.key
      || value.id
      || fallback
    );
  }
  return fallback;
}

export function formatApiErrorMessage(detail, fallback = 'Request failed') {
  if (detail == null || detail === '') return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return item.msg || item.message || item.detail || null;
        }
        return null;
      })
      .filter(Boolean);
    return messages.length ? messages.join('. ') : fallback;
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || detail.detail || fallback;
  }
  return fallback;
}

export function getWorkflowName(workflow) {
  return renderText(workflow?.name, 'Untitled Workflow');
}

export function getWorkflowDescription(workflow) {
  return renderText(workflow?.description, '');
}

export function getWorkflowCategoryKey(workflow) {
  const category = workflow?.category;
  if (typeof category === 'string') return category;
  if (typeof category === 'object' && category !== null) {
    return category.key || category.id || category.name || category.value || '';
  }
  return renderText(category, '');
}

export function getWorkflowTriggerLabel(workflow) {
  const trigger = workflow?.trigger
    || workflow?.trigger_type
    || workflow?.triggerType
    || workflow?.trigger_event
    || workflow?.event_key
    || workflow?.eventKey;

  if (typeof trigger === 'object' && trigger !== null) {
    return trigger.event_key || trigger.key || trigger.name || trigger.label || trigger.type || '—';
  }

  return renderText(trigger);
}

export function getWorkflowSenderLabel(workflow) {
  const sender = workflow?.sender
    || workflow?.sender_scope
    || workflow?.sender_type
    || workflow?.senderType
    || workflow?.scope;
  return renderText(sender, 'Platform');
}

export function getWorkflowStatusValue(workflow) {
  const status = workflow?.status || workflow?.state;
  if (typeof status === 'object' && status !== null) {
    return String(status.value || status.name || status.label || 'draft').toLowerCase();
  }
  return renderText(status, 'draft').toLowerCase();
}

export function getWorkflowStatusLabel(workflow) {
  const value = getWorkflowStatusValue(workflow);
  const labels = {
    active: 'Active',
    paused: 'Paused',
    draft: 'Draft',
    archived: 'Archived',
    failed: 'Failed',
  };
  return labels[value] || value.charAt(0).toUpperCase() + value.slice(1);
}

export function getWorkflowFolderName(workflow, foldersById = {}) {
  if (!workflow) return 'Unfiled';

  if (workflow.folder_id && foldersById[workflow.folder_id]) {
    return renderText(foldersById[workflow.folder_id].name, 'Unfiled');
  }

  const folder = workflow.folder
    || workflow.folder_name
    || workflow.folderName
    || workflow.category;

  if (typeof folder === 'object' && folder !== null) {
    return folder.name || folder.label || folder.title || folder.id || 'Unfiled';
  }

  if (typeof folder === 'string' && folder) {
    return folder.replace(/_/g, ' ');
  }

  return 'Unfiled';
}

export function getWorkflowFolderId(workflow) {
  if (workflow?.folder_id) return workflow.folder_id;
  if (workflow?.folderId) return workflow.folderId;
  if (typeof workflow?.folder === 'object' && workflow.folder?.id) return workflow.folder.id;
  return null;
}

export function normalizeWorkflowList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.workflows)) return data.workflows;
  return [];
}

export function normalizeFolderList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.folders)) return data.folders;
  return [];
}

export function countWorkflowsInFolder(workflows, folderId, foldersById = {}) {
  const list = Array.isArray(workflows) ? workflows : [];
  if (folderId === ALL_WORKFLOWS_FOLDER_ID) {
    return list.filter((w) => getWorkflowStatusValue(w) !== 'archived').length;
  }
  if (folderId === ARCHIVED_FOLDER_ID) {
    return list.filter((w) => getWorkflowStatusValue(w) === 'archived').length;
  }
  if (folderId === UNFILED_FOLDER_ID) {
    return list.filter((w) => !getWorkflowFolderId(w) && getWorkflowStatusValue(w) !== 'archived').length;
  }
  return list.filter((w) => getWorkflowFolderId(w) === folderId && getWorkflowStatusValue(w) !== 'archived').length;
}

export function filterWorkflowsByFolder(workflows, folderId) {
  const list = Array.isArray(workflows) ? workflows : [];
  if (folderId === ALL_WORKFLOWS_FOLDER_ID) {
    return list.filter((w) => getWorkflowStatusValue(w) !== 'archived');
  }
  if (folderId === ARCHIVED_FOLDER_ID) {
    return list.filter((w) => getWorkflowStatusValue(w) === 'archived');
  }
  if (folderId === UNFILED_FOLDER_ID) {
    return list.filter((w) => !getWorkflowFolderId(w) && getWorkflowStatusValue(w) !== 'archived');
  }
  return list.filter((w) => getWorkflowFolderId(w) === folderId && getWorkflowStatusValue(w) !== 'archived');
}

export function formatWorkflowUpdatedAt(value) {
  if (!value) return '—';
  if (typeof value === 'object' && value !== null) {
    const normalized = value.iso || value.date || value.value || value.timestamp;
    if (normalized) return formatWorkflowUpdatedAt(normalized);
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
